import apiProxy from "@shared/utils/api-proxy";
import { type UsersService } from "@shared/types/services/users";
import {
  type AdminApiTokenResponse,
  type GenerateTokenAdminApiProxyRequest,
  type RequireCredentialActionsRequest,
  type UserDTO,
} from "@shared/types/common";
import { useAuthStore } from "@shared/stores/auth";
import { useEngine } from "@shared/use/useEngine.ts";
import logger from "@shared/utils/logger.ts";
import { useKeycloakStore } from "@shared/stores/keycloak.ts";
import { analyticsService } from "@shared/services/analytics.ts";
import { useRouter } from "vue-router";
import { RouteNames } from "@shared/types/localTypes/router.ts";
import { useNotifications } from "@shared/module-transformers/useNotifications.ts";
import { useSessionLogic } from "@shared/use/useSessionLogic.ts";
import {
  isSamlTokenPayload,
  getNamePartsFromEmail,
} from "@shared/use/useSamlUser.ts";

const applySamlNameOverride = (user: UserDTO): UserDTO => {
  if (
    !isSamlTokenPayload(useKeycloakStore().tokenPayload) ||
    !user.email ||
    (user.firstName?.length && user.lastName?.length)
  )
    return user;

  const { firstName, lastName } = getNamePartsFromEmail(user.email);
  return {
    ...user,
    firstName,
    lastName,
  };
};

export const usersService: UsersService = {
  loginTour: async ({ userIdentifier }) => {
    return await apiProxy.post(
      `user/login?engine=${useEngine().engine}&mode=tour`,
      {
        userIdentifier,
        password: "",
      },
    );
  },
  proxyLogout: async () => {
    if (useSessionLogic().isKeycloakSession()) {
      await useKeycloakStore().logout();
    }
    await apiProxy.post("user/logout", {});
  },

  generateAdminApiToken: async (request: GenerateTokenAdminApiProxyRequest) => {
    const response: AdminApiTokenResponse | null = await apiProxy.post(
      "user/generateAdminApiToken",
      request,
    );
    if (!response) {
      throw new Error("Got no response from server");
    }
    return response;
  },

  getAdminApiToken: async (request: GenerateTokenAdminApiProxyRequest) => {
    const response: AdminApiTokenResponse | null = await apiProxy.post(
      "user/getAdminApiToken",
      request,
    );
    if (!response) {
      throw new Error("Got no response from server");
    }
    return response;
  },
  forgotPassword: async (forgotPasswordRequest) =>
    await apiProxy.post(`user/forgotPassword`, forgotPasswordRequest),

  resetPassword: async (passwordRequest) =>
    await apiProxy.post(`user/resetPassword`, passwordRequest),
  validateEmail: async (email) =>
    await apiProxy.post(`user/validateEmail`, { email }),

  changePassword: async (changePassword) =>
    await apiProxy.post(`user/changePassword`, changePassword),

  hasUser() {
    return !!useAuthStore().user;
  },

  fetchUser: async () => await apiProxy.get("user"),

  updatePersonalInfo: async (userDetails) =>
    await apiProxy.put(`user`, userDetails),

  reloadUser: async () => {
    return useAuthStore().setUser(await usersService.fetchUser());
  },

  refreshVueXUser: async (user) => useAuthStore().setUser(user),

  checkRegistrationToken: async (token) =>
    await apiProxy.get(`user/checkRegistrationToken/${token}`),

  register: async (registerRequest) =>
    await apiProxy.post(`user/register`, registerRequest),

  registerAndLoginSsoUser: async (ssoRegistrationRequest) =>
    await apiProxy.post(
      `user/registerAndLoginSsoUser?engine=${useEngine().engine}`,
      ssoRegistrationRequest,
    ),

  logRequestHeaders: async (logPrefix) =>
    await apiProxy.post(`user/logRequestHeaders`, { logPrefix }),

  setUser: async (user: UserDTO) => {
    const authStore = useAuthStore();
    authStore.setUser(user);
  },
  getOrFetchUser: async () => {
    let user = useAuthStore().user;
    if (user) {
      return user;
    }
    try {
      user = await usersService.fetchUser();
    } catch (e) {
      logger.error("Failed to fetch user", e);
      return null;
    }

    if (user) {
      user = applySamlNameOverride(user);
    }
    await useAuthStore().setUser(user);
    return user;
  },

  partnerLogOut: async () => {
    await useKeycloakStore().logout();
    analyticsService.clearAll();
    await useNotifications().closeAll();

    await useRouter().push({ name: RouteNames.Login });
  },
  requireCredentialActions: async (
    request: RequireCredentialActionsRequest,
  ) => {
    await apiProxy.post(`user/require-credential-actions`, request);
  },
};
